/**
 * useGA4 —— GA4 Measurement Protocol 客户端封装(Vue 2 SPA 版)
 *
 * 双通道设计:
 * - gtag.js 自动:由 src/utils/ga4-init.js 加载,自动打 page_view
 * - 本模块手动:发自定义事件(view_item 等)+ 镜像到后端 /api/v1/report/ga4
 *
 * 关键决策:
 * - session_id 走自管(纯数字时间戳,30 分钟无活动新会话)
 *   原计划走 gtag('get', 'session_id', cb) 拿 gtag 原生值,但测了环境 gtag
 *   从未发过事件,get 永远拿到空值/超时。自管一个纯数字 session_id,
 *   功能上等价(同会话稳定),mp/collect 不校验格式,代价是不跟 gtag 后台
 *   数据 100% 合并(本来也合并不了)
 * - CORS preflight:mp/collect 不响应 OPTIONS,Blob type 用 text/plain,
 *   fetch fallback 不设 Content-Type
 * - 优先 navigator.sendBeacon,失败再走 fetch keepalive + no-cors
 *
 * 用法:
 *   import { useGA4 } from '@/utils/ga4'
 *   const ga4 = useGA4()
 *   ga4.trackEvent('tutorial_begin')
 *   ga4.trackEvent('purchase', { transaction_id: 'T-1', value: 9.99, currency: 'USD' })
 *   ga4.trackEventWithUserData('login', { method: 'email' }, { email: 'a@b.com' })
 */

import CryptoJS from 'crypto-js'
import { getGA4Config } from './ga4-config'
import { fetchGa4Report } from '@/api'

// 缓存 client_id / session_id,避免每个事件都走 gtag.get / sessionStorage
var _clientIdCache = null
var _sessionIdCache = null

/**
 * 从 gtag.js 拿 client_id(优先缓存,缓存 miss 时异步回调填充)
 * gtag 未加载时返回 fallback
 */
function getGtagClientId() {
  return new Promise(function (resolve) {
    if (_clientIdCache) {
      resolve(_clientIdCache)
      return
    }
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      var fallback = Date.now() + '.' + Math.floor(Math.random() * 1e9)
      resolve(fallback)
      return
    }
    try {
      window.gtag('get', getGA4Config().measurementId, 'client_id', function (cid) {
        if (cid) _clientIdCache = cid
        resolve(cid || _clientIdCache || (Date.now() + '.' + Math.floor(Math.random() * 1e9)))
      })
      // gtag GET 异常兜底:2 秒后强制 resolve
      setTimeout(function () {
        if (!_clientIdCache) {
          _clientIdCache = Date.now() + '.' + Math.floor(Math.random() * 1e9)
        }
        resolve(_clientIdCache)
      }, 2000)
    } catch (e) {
      resolve(Date.now() + '.' + Math.floor(Math.random() * 1e9))
    }
  })
}

// 30 分钟无活动触发新会话 —— 跟 GA4 默认一致
var SESSION_TIMEOUT_MS = 30 * 60 * 1000
var SESSION_START_KEY = 'ga4_session_start_ts'

/**
 * 自管 session_id —— 模仿 GA4 默认会话行为(纯数字时间戳,30 分钟无活动新会话)
 * sessionStorage 持久化(tab 内稳定),内存缓存(每事件 O(1))
 */
function getSelfManagedSessionId() {
  if (_sessionIdCache) return _sessionIdCache
  if (typeof window === 'undefined') {
    return Date.now() + '-' + Math.random().toString(36).slice(2, 10)
  }
  var now = Date.now()
  var last = +(sessionStorage.getItem(SESSION_START_KEY) || 0)
  if (!last || now - last > SESSION_TIMEOUT_MS) {
    sessionStorage.setItem(SESSION_START_KEY, String(now))
    _sessionIdCache = String(now)
  } else {
    _sessionIdCache = String(last)
  }
  return _sessionIdCache
}

// ============ UPD 哈希工具 ============

/**
 * SHA-256 哈希(返回 hex 字符串,GA4 要求)
 */
export function sha256(input) {
  if (!input) return ''
  return CryptoJS.SHA256(input.trim().toLowerCase()).toString(CryptoJS.enc.Hex)
}

/**
 * 邮箱标准化:小写、去掉前后空格、本地部分去掉点
 */
function normalizeEmail(email) {
  if (!email) return ''
  var trimmed = email.trim().toLowerCase()
  var parts = trimmed.split('@')
  var local = parts[0]
  var domain = parts[1]
  if (!local || !domain) return trimmed
  // gmail 不去点(行业惯例),其他域去掉
  var cleanedLocal = domain === 'gmail.com' ? local : local.replace(/\./g, '')
  return cleanedLocal + '@' + domain
}

/**
 * 电话标准化:E164 格式(+国家码 + 号码)
 * 国内 11 位手机号默认 +86;海外业务改这里
 */
function normalizePhone(phone) {
  if (!phone) return ''
  var cleaned = phone.replace(/[\s\-()]/g, '')
  if (cleaned.startsWith('+')) return cleaned
  if (/^1[3-9]\d{9}$/.test(cleaned)) return '+86' + cleaned
  return '+' + cleaned
}

/**
 * 把原始 UPD 转成 GA4 要求的 hash 字段
 */
export function buildUserData(input) {
  var out = {}

  if (input.email) {
    var emails = Array.isArray(input.email) ? input.email : [input.email]
    out.sha256_email_address = emails.map(normalizeEmail).filter(Boolean).map(sha256)
  }

  if (input.phone) {
    var phones = Array.isArray(input.phone) ? input.phone : [input.phone]
    out.sha256_phone_number = phones.map(normalizePhone).filter(Boolean).map(sha256)
  }

  var addressObj = {}
  if (input.first_name) addressObj.sha256_first_name = sha256(input.first_name)
  if (input.last_name) addressObj.sha256_last_name = sha256(input.last_name)
  if (input.street) addressObj.sha256_street = sha256(input.street)
  // city / region / postal_code / country 明文,不需要哈希
  if (input.city) addressObj.city = input.city.trim().toLowerCase()
  if (input.region) addressObj.region = input.region.trim().toLowerCase()
  if (input.postal_code) addressObj.postal_code = input.postal_code.trim().toLowerCase()
  if (input.country) addressObj.country = input.country.trim().toLowerCase()

  var hasPlain = !!(input.city || input.region || input.postal_code || input.country)
  var hasHash = !!(input.first_name || input.last_name || input.street)
  if (hasHash || hasPlain) out.address = addressObj

  return out
}

// ============ 发送 ============

function postToGA4(payload) {
  return new Promise(function (resolve) {
    var measurementId = getGA4Config().measurementId
    if (!measurementId) {
      console.warn('[GA4] measurementId missing, event dropped')
      return resolve()
    }
    var apiSecret = getGA4Config().apiSecret
    if (!apiSecret) {
      console.warn('[GA4] api_secret missing, event dropped')
      return resolve()
    }

    var debug = getGA4Config().debug
    var endpoint = debug ? 'debug/mp/collect' : 'mp/collect'
    var url = 'https://www.google-analytics.com/' + endpoint +
      '?measurement_id=' + encodeURIComponent(measurementId) +
      '&api_secret=' + encodeURIComponent(apiSecret)
    var body = JSON.stringify(payload)

    // 优先 sendBeacon —— 页面关闭时仍能发出
    // Blob 的 type 必须是 CORS safelisted Content-Type(text/plain / form-urlencoded /
    // multipart/form-data)之一,否则浏览器发 OPTIONS preflight,mp/collect 不响应 → CORS 失败
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        var blob = new Blob([body], { type: 'text/plain' })
        var ok = navigator.sendBeacon(url, blob)
        if (ok) return resolve()
      } catch (e) { /* fallthrough */ }
    }

    // fallback: fetch keepalive(no-cors 让 GA4 不返回响应)
    // 不设 Content-Type,让浏览器走默认 text/plain
    try {
      fetch(url, {
        method: 'POST',
        body: body,
        keepalive: true,
        mode: 'no-cors'
      }).then(function () { resolve() }).catch(function (e) {
        console.warn('[GA4] send failed', e)
        resolve()
      })
    } catch (e) {
      console.warn('[GA4] send failed', e)
      resolve()
    }
  })
}

// ============ 主入口 ============

/**
 * Vue 2 SPA 没有 SSR 概念,直接导出实现
 * 调用方式:const ga4 = useGA4(); ga4.trackEvent('xxx', { ... })
 */
export function useGA4() {
  /**
   * 把事件镜像到后端 /api/v1/report/ga4
   * 失败不阻塞 —— 后端埋点只是数据备份,主链路是 mp/collect
   * 不 await,fire-and-forget,.catch 兜底避免 unhandled rejection
   */
  function reportToBackend(name, clientId, sessionId) {
    fetchGa4Report({
      properties: {
        client_id: clientId,
        session_id: sessionId
      }
    }).catch(function () { /* 后端埋点失败不影响主链路 */ })
  }

  /**
   * 自动给 params 补 session_id / engagement_time_msec
   * —— 否则 GA4 实时报告看不到活动,互动指标也不准
   */
  function enrichParams(params) {
    var enriched = Object.assign({}, params || {})
    if (!enriched.session_id) {
      enriched.session_id = getSelfManagedSessionId()
    }
    if (enriched.engagement_time_msec == null) {
      var last = window.__ga4_last_engagement || Date.now()
      enriched.engagement_time_msec = Math.max(1, Date.now() - last)
    }
    window.__ga4_last_engagement = Date.now()
    return enriched
  }

  /**
   * 通用事件发送
   */
  function trackEvent(name, params) {
    params = params || {}
    return getGtagClientId().then(function (clientId) {
      var enrichedParams = enrichParams(params)
      return postToGA4({
        client_id: clientId,
        events: [{ name: name, params: enrichedParams }]
      }).then(function () {
        reportToBackend(name, clientId, String(enrichedParams.session_id || ''))
      })
    })
  }

  /**
   * 带 UPD 的事件(user_id + user_data 同时传,否则 GA4 会拒)
   * 典型场景:登录、注册、下单成功,把用户信息回流做模型转化分析
   */
  function trackEventWithUserData(name, params, userData, userId) {
    params = params || {}
    return getGtagClientId().then(function (clientId) {
      var enrichedParams = enrichParams(params)
      var payload = {
        client_id: clientId,
        events: [{ name: name, params: enrichedParams }],
        user_data: buildUserData(userData)
      }
      if (userId) payload.user_id = userId
      return postToGA4(payload).then(function () {
        reportToBackend(name, clientId, String(enrichedParams.session_id || ''))
      })
    })
  }

  /**
   * 仅发送 UPD(不带事件),用于首次建立用户档案
   */
  function sendUserData(userData, userId) {
    return getGtagClientId().then(function (clientId) {
      var payload = {
        client_id: clientId,
        user_data: buildUserData(userData)
      }
      if (userId) payload.user_id = userId
      return postToGA4(payload)
    })
  }

  /**
   * 手动设置 user_id,登录成功后调用(让后续事件自动带上)
   */
  function setUserId(userId) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('set', { user_id: userId })
    }
  }

  /**
   * 页面浏览(给 SPA 路由切换用,首屏交给 gtag.js 自动打)
   */
  function trackPageView(pagePath, pageTitle) {
    return trackEvent('page_view', {
      page_location: pagePath || window.location.href,
      page_title: pageTitle || document.title
    })
  }

  return {
    trackEvent: trackEvent,
    trackEventWithUserData: trackEventWithUserData,
    sendUserData: sendUserData,
    setUserId: setUserId,
    trackPageView: trackPageView
  }
}
